// data-layer/mutations/auth.ts

import type { AxiosError, AxiosProgressEvent } from 'axios';
import type { ApiImportData, ApiResponse, Driver, DriverListParams, DriverPayload } from '@dc-trans/data-layer';
import {
  createDriverData,
  exportDriverData,
  importDriverData,
  resetDriverPassword,
  updateDriverData,
} from '@dc-trans/data-layer';
import { useMutation } from '@tanstack/react-query';
import { mergeApiErrorNotificationMeta, type MutationOptionsWithNotification } from '@/mutations/utils';

// The custom hook that you will use in your components
export const useCreateDriverMutation = (
  options?: MutationOptionsWithNotification<
    ApiResponse<Driver>,
    AxiosError<ApiResponse<Driver>>,
    Partial<DriverPayload>
  >
) => {
  const { disableErrorNotification, meta, ...restOptions } = options ?? {};
  const mergedMeta = mergeApiErrorNotificationMeta(
    {
      message: 'Gagal memproses data driver.',
    },
    meta,
    disableErrorNotification
  );

  return useMutation<ApiResponse<Driver>, AxiosError<ApiResponse<Driver>>, Partial<DriverPayload>>({
    ...restOptions,
    mutationFn: createDriverData,
    meta: mergedMeta,
  });
};

export const useUpdateDriverMutation = (
  options?: MutationOptionsWithNotification<
    ApiResponse<Driver>,
    AxiosError<ApiResponse<Driver>>,
    Partial<DriverPayload>
  >
) => {
  const { disableErrorNotification, meta, ...restOptions } = options ?? {};
  const mergedMeta = mergeApiErrorNotificationMeta(
    {
      message: 'Gagal memperbarui data driver.',
    },
    meta,
    disableErrorNotification
  );

  return useMutation<ApiResponse<Driver>, AxiosError<ApiResponse<Driver>>, Partial<DriverPayload>>({
    ...restOptions,
    mutationFn: updateDriverData,
    meta: mergedMeta,
  });
};

export const useResetDriverPasswordMutation = (
  options?: MutationOptionsWithNotification<ApiResponse<Driver>, AxiosError<ApiResponse<Driver>>, string>
) => {
  const { disableErrorNotification, meta, ...restOptions } = options ?? {};
  const mergedMeta = mergeApiErrorNotificationMeta(
    {
      message: 'Gagal mereset password driver.',
    },
    meta,
    disableErrorNotification
  );

  return useMutation<ApiResponse<Driver>, AxiosError<ApiResponse<Driver>>, string>({
    ...restOptions,
    mutationFn: resetDriverPassword,
    meta: mergedMeta,
  });
};

// In data-layer: Create a mutation hook
export const useDownloadDriverData = (
  options?: MutationOptionsWithNotification<
    {
      data: any;
      filename: string;
    },
    AxiosError<{
      data: any;
      filename: string;
    }>,
    DriverListParams
  >
) => {
  const { disableErrorNotification, meta, ...restOptions } = options ?? {};
  const mergedMeta = mergeApiErrorNotificationMeta(
    {
      message: 'Gagal mengunduh data driver.',
    },
    meta,
    disableErrorNotification
  );

  return useMutation<
    {
      data: any;
      filename: string;
    },
    AxiosError<{
      data: any;
      filename: string;
    }>,
    DriverListParams
  >({
    ...restOptions,
    mutationFn: exportDriverData,
    meta: mergedMeta,
  });
};

export const useUploadDriverData = (
  options?: MutationOptionsWithNotification<
    ApiResponse<ApiImportData>,
    AxiosError<ApiResponse<ApiImportData>>,
    {
      data: FormData;
      progressCallback: (_progressEvent: AxiosProgressEvent) => void;
    }
  >
) => {
  const { disableErrorNotification, meta, ...restOptions } = options ?? {};
  const mergedMeta = mergeApiErrorNotificationMeta(
    {
      message: 'Gagal mengunggah data driver.',
    },
    meta,
    disableErrorNotification
  );

  return useMutation<
    ApiResponse<ApiImportData>,
    AxiosError<ApiResponse<ApiImportData>>,
    {
      data: FormData;
      progressCallback: (_progressEvent: AxiosProgressEvent) => void;
    }
  >({
    ...restOptions,
    mutationFn: importDriverData,
    meta: mergedMeta,
  });
};
